import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  getGeneralTasks,
  createGeneralTask,
  updateGeneralTask,
  deleteGeneralTask,
} from '../services/api';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import { useTranslation } from 'react-i18next';
import { uniqueCategoriesInSortOrder, compareCatalogTasks } from '../utils/generalTasks';

function isSystemTask(task) {
  return Boolean(task?.isMaster || task?.organisationId == null);
}

function GeneralTasks() {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const categoryOptions = useMemo(() => uniqueCategoriesInSortOrder(tasks), [tasks]);

  const filteredTasks = useMemo(() => {
    return (tasks || [])
      .filter((task) => {
        if (categoryFilter) {
          if ((task.category || '').toLowerCase() !== categoryFilter.toLowerCase()) {
            return false;
          }
        }
        if (sourceFilter === 'system' && !isSystemTask(task)) return false;
        if (sourceFilter === 'created' && isSystemTask(task)) return false;
        return true;
      })
      .slice()
      .sort(compareCatalogTasks);
  }, [tasks, categoryFilter, sourceFilter]);

  const columns = [
    {
      field: 'source',
      headerName: t('generalTasks.fields.source'),
      width: 120,
      valueGetter: (params) =>
        isSystemTask(params.row)
          ? t('generalTasks.sourceSystem')
          : t('generalTasks.sourceCreated'),
    },
    { field: 'category', headerName: t('generalTasks.fields.category'), width: 200 },
    { field: 'name', headerName: t('generalTasks.fields.name'), flex: 1, minWidth: 200 },
    { field: 'description', headerName: t('generalTasks.fields.description'), flex: 1.2, minWidth: 240 },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 200,
      sortable: false,
      renderCell: (params) => {
        if (isSystemTask(params.row)) return null;
        return (
          <Box>
            <Button onClick={() => handleEdit(params.row)}>{t('common.edit')}</Button>
            <Button color="error" onClick={() => handleDelete(params.row.id)}>{t('common.delete')}</Button>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await getGeneralTasks();
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching general tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setEditingTask(null);
    setName('');
    setCategory('');
    setDescription('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTask(null);
    setName('');
    setCategory('');
    setDescription('');
  };

  const handleEdit = (task) => {
    if (isSystemTask(task)) return;
    setEditingTask(task);
    setName(task.name || '');
    setCategory(task.category || '');
    setDescription(task.description || '');
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('generalTasks.deleteConfirm'))) return;
    try {
      await deleteGeneralTask(id);
      fetchTasks();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      setSaving(true);
      const payload = {
        name: trimmed,
        category: category.trim(),
        description: description.trim(),
      };
      if (editingTask) {
        await updateGeneralTask(editingTask.id, payload);
      } else {
        await createGeneralTask(payload);
      }
      handleClose();
      fetchTasks();
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsivePageSection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Typography variant="h4">{t('generalTasks.title')}</Typography>
        <Button variant="contained" onClick={handleOpen}>{t('generalTasks.addTask')}</Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="general-tasks-category-filter">{t('generalTasks.filterByCategory')}</InputLabel>
          <Select
            labelId="general-tasks-category-filter"
            label={t('generalTasks.filterByCategory')}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">{t('generalTasks.allCategories')}</MenuItem>
            {categoryOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="general-tasks-source-filter">{t('generalTasks.filterBySource')}</InputLabel>
          <Select
            labelId="general-tasks-source-filter"
            label={t('generalTasks.filterBySource')}
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <MenuItem value="">{t('generalTasks.allSources')}</MenuItem>
            <MenuItem value="system">{t('generalTasks.sourceSystem')}</MenuItem>
            <MenuItem value="created">{t('generalTasks.sourceCreated')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isMobile ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataList
            rows={filteredTasks}
            getTitle={(task) => task.name}
            getSubtitle={(task) =>
              [
                isSystemTask(task) ? t('generalTasks.sourceSystem') : t('generalTasks.sourceCreated'),
                task.category,
                task.description,
              ]
                .filter(Boolean)
                .join(' — ') || undefined
            }
            renderActions={(task) =>
              isSystemTask(task) ? null : (
                <>
                  <Button size="small" onClick={() => handleEdit(task)}>{t('common.edit')}</Button>
                  <Button size="small" color="error" onClick={() => handleDelete(task.id)}>
                    {t('common.delete')}
                  </Button>
                </>
              )
            }
          />
        )
      ) : (
        <DataGrid
          rows={filteredTasks}
          columns={columns}
          pageSize={25}
          loading={loading}
          autoHeight
          disableSelectionOnClick
        />
      )}

      {!loading && filteredTasks.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('generalTasks.emptySection')}
        </Typography>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingTask ? t('generalTasks.editTask') : t('generalTasks.addTask')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              freeSolo
              options={categoryOptions}
              value={category}
              onChange={(_event, value) => setCategory(value || '')}
              onInputChange={(_event, value) => setCategory(value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('generalTasks.fields.category')}
                  helperText={t('generalTasks.categoryFreeSoloHint')}
                />
              )}
            />
            <TextField
              autoFocus
              fullWidth
              label={t('generalTasks.fields.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('generalTasks.fields.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={saving || !name.trim()}>
              {t('common.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </ResponsivePageSection>
  );
}

export default GeneralTasks;
